/**
 * Stock photography, hosted on the Pexels CDN.
 *
 * Used ONLY where the client's own gallery has no photo that matches a service
 * exactly — see `gallery-data.ts` and prefer a real job photo whenever one is a
 * true match. Every image here is a real photograph: no illustrations, no
 * AI-generated art, no generic vector graphics.
 *
 * Pexels License: free for commercial use, no attribution required.
 * https://www.pexels.com/license/
 *
 * URLs are hotlinked to the Pexels CDN, which they permit. If the site ever
 * needs to be fully self-contained, download these IDs into public/assets and
 * swap the base path below.
 */

const PEXELS_BASE = "https://images.pexels.com/photos";

const stock = (id: number, w: number, h: number) =>
  `${PEXELS_BASE}/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&fit=crop&w=${w}&h=${h}`;

/** Service and blog artwork — 1600x1000. */
export const stockCard = (id: number) => stock(id, 1600, 1000);

/** Full-bleed page heroes — 1920x1080. */
export const stockHero = (id: number) => stock(id, 1920, 1080);

/**
 * Photo IDs, named by what they actually show so assignments stay auditable.
 *
 * Each was reviewed for quality and subject match before use. Every photo here
 * is also checked to show NO identifiable faces and NO third-party branding —
 * only the client's own gallery photos may show people or branding. When
 * swapping any of these, re-check that rule: several otherwise-good stock
 * cleaning photos feature models in branded uniforms.
 */
export const PHOTO = {
  /** Gloved hands spraying a chrome bathroom faucet — deep-clean detail work. */
  deepCleanDetail: 4239130,
  /** Bright, spacious, tidy open-plan living room. No people. */
  tidyLivingRoom: 7214166,
  /** Wall of neatly racked mops and labelled supply bins — a team on a schedule. */
  organizedCleaningTools: 7108400,
  /** Cleaner in a hooded protective suit vacuuming a living room. Face not visible. */
  vacuumingLivingRoom: 6195951,
  /** Contemporary open-plan office with desks and task chairs. No people. */
  officeInterior: 9300768,
  /** Empty modern restaurant dining room, tables set. No people, no signage. */
  emptyRestaurant: 20505442,
  /** Bright, modern medical examination room. No people. */
  clinicExamRoom: 7789601,
  /**
   * White walk-in closet with clothes grouped, shoes lined up, bags shelved.
   * Chosen over the glossier showroom shots, which read as CGI renders rather
   * than photographs. No people, no legible branding.
   */
  organizedCloset: 5705490,
  /** Bright guest bedroom with a freshly made bed. No people. */
  madeBedroom: 34688212,
  /**
   * Empty room, cleaned and ready for handover — gleaming floor, clean
   * baseboards and vents. Replaced a dim gallery photo of the same subject.
   */
  emptyCleanedRoom: 6835181,
  /** Empty room with warm hardwood opening onto a second room, cleaned out. */
  emptyRoomAfterMove: 3935327,
  /** Gloved hands scrubbing a stovetop. No face. */
  scrubbingStove: 9462302,
  /** Hands loading a front-load washing machine in a laundry room. No face, no branding. */
  laundryLoadingWasher: 5591581,
} as const;
